import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useColorMode } from '../ThemeContext';
import { db } from '../firebase';
import { formatPropertyDate } from '../utils/formatDate';

interface Measurement {
  id: string;
  date: string;
  totalGallons: number;
  totalCost: number;
  totalBillCents?: number;
  createdAt?: { seconds: number } | string;
}

// Shape of property-level invoice docs written by the backend
// (src/routes/invoices.ts). Invoices live at
// clients/{uid}/properties/{propertyId}/invoices — NOT under tenants.
interface InvoiceDocData {
  totalAmountCents: number;
  tenantName?: string;
  status: string;
  metadata?: { measurementId?: string; tenantId?: string };
}

interface InvoiceRow extends InvoiceDocData {
  id: string;
  tenantName: string;
}

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  paid: 'success',
  failed: 'error',
  returned: 'error',
  void: 'default',
  draft: 'default',
};

function statusIconFor(status: string): React.ReactElement {
  switch (STATUS_COLORS[status] ?? 'default') {
    case 'success':
      return <CheckCircleOutlineIcon sx={{ fontSize: '12px !important' }} />;
    case 'error':
      return <ErrorOutlineIcon sx={{ fontSize: '12px !important' }} />;
    case 'warning':
      return <HourglassEmptyOutlinedIcon sx={{ fontSize: '12px !important' }} />;
    default:
      return <RemoveCircleOutlineIcon sx={{ fontSize: '12px !important' }} />;
  }
}

function formatDate(d: string, timeZone?: string | null) {
  return formatPropertyDate(d, timeZone);
}

function StatPill({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ color: 'primary.main', display: 'flex', opacity: 0.8 }}>{icon}</Box>
      <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function BillRow({
  measurement,
  invoices,
  timeZone,
}: {
  measurement: Measurement;
  invoices: InvoiceRow[];
  timeZone?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasInvoices = invoices.length > 0;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: hasInvoices ? 'pointer' : 'default',
          '&:hover': hasInvoices ? { bgcolor: 'rgba(37,99,235,0.02)' } : undefined,
        }}
        onClick={() => hasInvoices && setExpanded((v) => !v)}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatDate(measurement.date, timeZone)}
            </Typography>
            <Chip
              label={hasInvoices ? `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}` : 'No invoices'}
              color={hasInvoices ? 'success' : 'warning'}
              size="small"
              variant={hasInvoices ? 'filled' : 'outlined'}
              sx={{
                height: 20,
                fontSize: '0.68rem',
                ...(!hasInvoices && {
                  color: 'warning.dark',
                  borderColor: 'warning.dark',
                }),
              }}
            />
          </Stack>
          <Grid container spacing={3}>
            <Grid item>
              <StatPill
                icon={<OpacityOutlinedIcon sx={{ fontSize: 14 }} />}
                value={`${measurement.totalGallons.toLocaleString()} gal`}
              />
            </Grid>
            <Grid item>
              <StatPill
                icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 14 }} />}
                value={`$${measurement.totalCost.toFixed(2)}`}
              />
            </Grid>
          </Grid>
        </Box>
        {hasInvoices && (
          <Tooltip title={expanded ? 'Collapse' : 'View invoices'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              aria-label={expanded ? 'Collapse invoices' : 'View invoices'}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Invoice table */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', py: 1 }}>
                  TENANT
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', py: 1 }}>
                  AMOUNT
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', py: 1 }}>
                  STATUS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ py: 1.25 }}>{inv.tenantName}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}
                  >
                    ${(inv.totalAmountCents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.25 }}>
                    <Chip
                      icon={statusIconFor(inv.status)}
                      label={inv.status}
                      color={STATUS_COLORS[inv.status] ?? 'default'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(37,99,235,0.12)' : '#F0F4FF' })}>
                <TableCell sx={{ fontWeight: 700, py: 1.25 }}>Total billed</TableCell>
                <TableCell
                  align="right"
                  sx={{ py: 1.25, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}
                >
                  ${(invoices.reduce((s, i) => s + i.totalAmountCents, 0) / 100).toFixed(2)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PropertyHistory() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode, toggleMode } = useColorMode();

  const [propertyName, setPropertyName] = useState<string | null>(null);
  const [propertyTimeZone, setPropertyTimeZone] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[] | null>(null);
  const [invoicesByMeasurement, setInvoicesByMeasurement] = useState<Record<string, InvoiceRow[]>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user || !propertyId) return;
    try {
      // Load property name
      const propSnap = await getDoc(doc(db, 'clients', user.uid, 'properties', propertyId));
      if (propSnap.exists()) {
        const d = propSnap.data() as { name?: string; timeZone?: string };
        setPropertyName(d.name ?? propertyId);
        setPropertyTimeZone(d.timeZone ?? null);
      }

      // Load measurements
      const measSnap = await getDocs(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'measurements'),
      );
      const mesList: Measurement[] = measSnap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Measurement, 'id'>),
      }));
      mesList.sort((a, b) => {
        const da = new Date(a.date).getTime();
        const db2 = new Date(b.date).getTime();
        return db2 - da;
      });
      setMeasurements(mesList);

      // Load property-level invoices. The backend writes invoices to
      // clients/{uid}/properties/{propertyId}/invoices (see
      // src/routes/invoices.ts), with tenant identity carried on each doc.
      const invSnap = await getDocs(
        collection(db, 'clients', user.uid, 'properties', propertyId, 'invoices'),
      );

      const byMeasurement: Record<string, InvoiceRow[]> = {};

      invSnap.docs.forEach((invDoc) => {
        const invData = invDoc.data() as InvoiceDocData;
        const measId = invData.metadata?.measurementId ?? '__unassociated__';
        if (!byMeasurement[measId]) byMeasurement[measId] = [];
        byMeasurement[measId].push({
          ...invData,
          id: invDoc.id,
          tenantName: invData.tenantName || invData.metadata?.tenantId || 'Unknown tenant',
        });
      });

      setInvoicesByMeasurement(byMeasurement);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing history');
    }
  }, [user, propertyId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Go back">
            <IconButton
              color="inherit"
              onClick={() => navigate(`/properties/${propertyId}`)}
              edge="start"
              aria-label="Go back"
              sx={{ mr: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              color="inherit"
              onClick={toggleMode}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, pb: 8 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
          >
            Billing history
          </Typography>
          {propertyName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {propertyName}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {measurements === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {measurements !== null && measurements.length === 0 && (
          <Box
            sx={{
              p: 5,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <ReceiptOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              No billing history yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Upload your first water bill to start tracking usage and invoices.
            </Typography>
            <Button variant="contained" onClick={() => navigate(`/properties/${propertyId}`)}>
              Upload a bill
            </Button>
          </Box>
        )}

        {measurements !== null && measurements.length > 0 && (
          <>
            {/* Summary stats */}
            <Grid container spacing={1.5} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">Billing cycles</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {measurements.length}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">Total invoiced</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    ${(
                      Object.values(invoicesByMeasurement)
                        .flat()
                        .reduce((s, i) => s + i.totalAmountCents, 0) / 100
                    ).toFixed(0)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">Invoices generated</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {Object.values(invoicesByMeasurement).flat().length}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">Paid invoices</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {Object.values(invoicesByMeasurement).flat().filter((i) => i.status === 'paid').length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Bills list */}
            <Stack spacing={1.5}>
              {measurements.map((m) => (
                <BillRow
                  key={m.id}
                  measurement={m}
                  invoices={invoicesByMeasurement[m.id] ?? []}
                  timeZone={propertyTimeZone}
                />
              ))}
            </Stack>
          </>
        )}
      </Container>
    </>
  );
}
